import * as React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { setUser } from '@sentry/react';
import { useGlobalStore } from 'src/hooks';
import { ROUTES, UI_ROUTES } from 'src/constants';
import { VisionrEmblem, MenuToggleIcon } from 'src/assets/icons';
import { useIsLoggedIn, useGetUserDetails } from 'src/hooks/api';
import { RoomProvider } from '@liveblocks/react/suspense';
import { signOutFn } from 'src/utils/sign-out';
import { StyledDrawerWrapper, StyledDrawer, StyledMain, StyledDrawerHeader, StyledLogo } from './Layout.styled';
import { IconButton, AuthenticatingState } from '../atoms';
import { Links } from './Links';
import { useMenuToggle } from './hooks/useMenuToggle';
import { NavBarActions } from './NavBarActions';
import { ErrorBoundary } from '../ErrorBoundary';
import { LiveCursors } from '../atoms/LiveBlockPresence/LiveBlockPresence';
import { FloatingCustomerCentreFileUploads } from '../CustomerCentre/CustomerCentreFoldersDetails/FloatingCustomerCentreFileUploads/FloatingCustomerCentreFileUploads';

export const Layout: React.FunctionComponent = () => {
  const { toggleMenu, menuIsOpen, handleMouseOver, handleMouseExist, drawerType, handleCloseMobileMenu } =
    useMenuToggle();
  const { userDetails } = useGetUserDetails();
  const navigate = useNavigate();

  const location = useLocation();

  const prefersWideDashboardMenu = useGlobalStore((state) => state.prefersWideDashboardMenu);
  const { isLoggedIn, isAuthenticating } = useIsLoggedIn();

  React.useEffect(() => {
    if (!userDetails?.email) return;

    setUser({
      username: userDetails?.email || userDetails?.username,
    });
  }, [userDetails?.email, userDetails?.username]);

  if (isAuthenticating) {
    return <AuthenticatingState />;
  }

  if (!isLoggedIn) {
    signOutFn();

    navigate({ pathname: `${UI_ROUTES.LOGIN}`, search: `next=${window.location.pathname}${window.location.search}` });
  }

  return (
    <RoomProvider
      id={`visionr:${location.pathname}`}
      initialPresence={{
        cursor: null,
      }}
    >
      <StyledDrawerWrapper>
        <StyledDrawer
          variant={drawerType}
          open={menuIsOpen}
          onMouseEnter={handleMouseOver}
          onMouseLeave={handleMouseExist}
          $prefersWideDashboardMenu={prefersWideDashboardMenu}
          onClose={toggleMenu}
        >
          {/* Dashboard Header */}
          <StyledDrawerHeader $prefersWideDashboardMenu={prefersWideDashboardMenu} $menuIsOpen={menuIsOpen}>
            {/* Org details */}
            <StyledLogo $menuIsOpen={menuIsOpen}>
              <VisionrEmblem />
            </StyledLogo>

            <IconButton onClick={toggleMenu} aria-label="toggle drawer">
              <MenuToggleIcon />
            </IconButton>
          </StyledDrawerHeader>

          {/* Dashboard links */}
          <Links menuIsOpen={menuIsOpen} dashboardLinks={ROUTES} handleCloseMobileMenu={handleCloseMobileMenu} />

          {/* NavBarActions - Profile, support buttons etc */}
          <NavBarActions menuIsOpen={menuIsOpen} toggleMenu={toggleMenu} />
        </StyledDrawer>

        {/* Dashboard main content */}
        <ErrorBoundary componentId="DashboardLayout-StyledMain">
          <StyledMain $prefersWideDashboardMenu={prefersWideDashboardMenu}>
            <LiveCursors>
              <Outlet />

              <FloatingCustomerCentreFileUploads />
            </LiveCursors>
          </StyledMain>
        </ErrorBoundary>
      </StyledDrawerWrapper>
    </RoomProvider>
  );
};
